import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Car, 
  User, 
  Phone, 
  MapPin, 
  Calendar, 
  Clock,
  DollarSign,
  CheckCircle,
  Loader2,
  RefreshCw,
  Play,
  Square,
  Timer,
  Plus
} from 'lucide-react';

const AdminDriveYourCar = () => {
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [waitTimeInput, setWaitTimeInput] = useState({});

  useEffect(() => {
    fetchBookings();
    fetchDrivers();
  }, []);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/drive-your-car/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/drivers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to fetch drivers');
    }
  };

  const assignDriver = async (bookingId, driverId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/drive-your-car/bookings/${bookingId}/assign?driver_id=${driverId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Driver assigned');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign driver');
    }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/drive-your-car/bookings/${bookingId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${status}`);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const startRide = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/drive-your-car/bookings/${bookingId}/time`, 
        { action: 'start_ride' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ride started! Timer running.');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start ride');
    }
  };

  const endRide = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/drive-your-car/bookings/${bookingId}/time`, 
        { action: 'end_ride' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Ride completed! Duration: ${response.data.actual_minutes} minutes`);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end ride');
    }
  };

  const addWaitTime = async (bookingId) => {
    const minutes = parseInt(waitTimeInput[bookingId]) || 0;
    if (minutes <= 0) {
      toast.error('Please enter wait time in minutes');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/drive-your-car/bookings/${bookingId}/time`, 
        { action: 'add_wait_time', wait_minutes: minutes, notes: 'Wait time added' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Added ${minutes} min wait time (+$${(minutes * 0.50).toFixed(2)})`);
      setWaitTimeInput(prev => ({ ...prev, [bookingId]: '' }));
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add wait time');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_payment': return 'bg-gold-100 text-gold-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-purple-100 text-purple-800';
      case 'in_progress': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getServiceLabel = (option) => {
    switch (option) {
      case 'drive_me_home': return '🍸 Drive Me Home';
      case 'take_car_home': return '🚗 Take Car Home';
      case 'personal_chauffeur': return '🎯 Personal Chauffeur';
      default: return 'Drive Your Car';
    }
  };

  const filteredBookings = bookings.filter(b => {
    if (filter === 'all') return true;
    if (filter === 'pending') return ['pending_payment', 'confirmed'].includes(b.status);
    if (filter === 'active') return ['assigned', 'in_progress'].includes(b.status);
    if (filter === 'completed') return b.status === 'completed';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Car className="h-6 w-6 text-purple-600" />
            Drive Your Car Bookings
          </h1>
          <p className="text-gray-500">Manage designated driver service bookings</p>
        </div>
        <Button onClick={fetchBookings} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilter('all')}>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{bookings.length}</div>
            <div className="text-sm text-gray-500">Total Bookings</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilter('pending')}>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-gold-600">
              {bookings.filter(b => ['pending_payment', 'confirmed'].includes(b.status)).length}
            </div>
            <div className="text-sm text-gray-500">Pending</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilter('active')}>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-orange-600">
              {bookings.filter(b => ['assigned', 'in_progress'].includes(b.status)).length}
            </div>
            <div className="text-sm text-gray-500">Active</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilter('completed')}>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">
              {bookings.filter(b => b.status === 'completed').length}
            </div>
            <div className="text-sm text-gray-500">Completed</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Pills */}
      <div className="flex gap-2">
        {['all', 'pending', 'active', 'completed'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === f ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              No bookings found
            </CardContent>
          </Card>
        ) : (
          filteredBookings.map((booking) => (
            <Card key={booking.id} className="overflow-hidden">
              <div className="flex flex-col lg:flex-row">
                {/* Main Info */}
                <div className="flex-1 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg font-semibold">{booking.client_name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {booking.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="text-sm text-purple-600 font-medium">
                        {getServiceLabel(booking.service_option)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">${booking.total_amount?.toFixed(2)}</div>
                      {booking.wait_time_charge > 0 && (
                        <div className="text-xs text-orange-600">
                          incl. ${booking.wait_time_charge?.toFixed(2)} wait time
                        </div>
                      )}
                      <div className="text-xs text-gray-500">
                        {booking.payment_status === 'paid' ? (
                          <span className="text-green-600 flex items-center justify-end gap-1"><CheckCircle className="h-3 w-3" /> Paid</span>
                        ) : (
                          <span className="text-gold-600 flex items-center justify-end gap-1"><Clock className="h-3 w-3" /> Payment Pending</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-gray-500">Pickup</div>
                        <div className="font-medium">{booking.pickup_location}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-gray-500">Dropoff</div>
                        <div className="font-medium">{booking.dropoff_location}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span>{booking.scheduled_date} at {booking.scheduled_time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <a href={`tel:${booking.client_phone}`} className="text-blue-600 hover:underline">{booking.client_phone}</a>
                    </div>
                  </div>

                  {booking.vehicle_make && (
                    <div className="mt-3 text-sm text-gray-600 flex items-center gap-2">
                      <Car className="h-4 w-4" />
                      <span className="font-medium">
                        {booking.vehicle_color} {booking.vehicle_year} {booking.vehicle_make} {booking.vehicle_model}
                      </span>
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">({booking.vehicle_type})</span>
                    </div>
                  )}

                  {booking.assigned_driver_name && (
                    <div className="mt-3 text-sm bg-purple-50 text-purple-700 px-3 py-2 rounded-lg inline-flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Driver: <strong>{booking.assigned_driver_name}</strong>
                    </div>
                  )}

                  {booking.total_wait_minutes > 0 && (
                    <div className="mt-2 text-sm bg-orange-50 text-orange-700 px-3 py-2 rounded-lg inline-flex items-center gap-2">
                      <Timer className="h-4 w-4" />
                      Wait Time: {booking.total_wait_minutes} min (${booking.wait_time_charge?.toFixed(2)})
                    </div>
                  )}
                </div>

                {/* Actions Panel */}
                <div className="bg-gray-50 p-4 lg:w-[280px] space-y-4">
                  {/* Assign Driver */}
                  {!booking.assigned_driver_id && (
                    <div>
                      <label className="text-xs text-gray-500 font-medium">Assign Driver</label>
                      <select
                        onChange={(e) => e.target.value && assignDriver(booking.id, e.target.value)}
                        className="w-full p-2 border rounded text-sm mt-1"
                        defaultValue=""
                      >
                        <option value="">Select driver...</option>
                        {drivers.map(d => (
                          <option key={d.id} value={d.id}>{d.name || d.email}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Ride Controls - for assigned rides */}
                  {booking.assigned_driver_id && booking.status !== 'completed' && (
                    <div className="space-y-2">
                      <label className="text-xs text-gray-500 font-medium">Ride Controls</label>
                      
                      {!booking.ride_started_at ? (
                        <Button 
                          onClick={() => startRide(booking.id)} 
                          className="w-full bg-green-600 hover:bg-green-700"
                          size="sm"
                        >
                          <Play className="h-4 w-4 mr-2" />
                          Start Ride
                        </Button>
                      ) : booking.status === 'in_progress' ? (
                        <>
                          <Button 
                            onClick={() => endRide(booking.id)} 
                            className="w-full bg-red-600 hover:bg-red-700"
                            size="sm"
                          >
                            <Square className="h-4 w-4 mr-2" />
                            End Ride
                          </Button>
                          
                          {/* Add Wait Time */}
                          <div className="flex gap-2 mt-2">
                            <Input
                              type="number"
                              placeholder="Min"
                              value={waitTimeInput[booking.id] || ''}
                              onChange={(e) => setWaitTimeInput(prev => ({ ...prev, [booking.id]: e.target.value }))}
                              className="w-20"
                            />
                            <Button 
                              onClick={() => addWaitTime(booking.id)}
                              variant="outline"
                              size="sm"
                              className="flex-1"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add Wait
                            </Button>
                          </div>
                          <p className="text-xs text-gray-500">$0.50/min wait charge</p>
                        </>
                      ) : null}
                    </div>
                  )}

                  {/* Status Update */}
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Update Status</label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {['confirmed', 'assigned', 'completed', 'cancelled'].map(status => (
                        <button
                          key={status}
                          onClick={() => updateStatus(booking.id, status)}
                          disabled={booking.status === status}
                          className={`px-2 py-1 text-xs rounded ${
                            booking.status === status 
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                              : 'bg-white border hover:bg-gray-100'
                          }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminDriveYourCar;
